import {
  ApplyPrimaryModelResponseSchema,
  ModelCatalogSchema,
  type ModelCatalog,
} from "@herskin/contracts";
import { SelectField } from "@herskin/ui";
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "./api";

export function ModelSelector() {
  const [catalog, setCatalog] = useState<ModelCatalog | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      setCatalog(ModelCatalogSchema.parse(await api<unknown>("/api/management/models")));
      setNotice("");
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : "主模型状态暂不可读");
    }
  }, []);

  useEffect(() => {
    void load();
    const refresh = () => void load();
    window.addEventListener("herskin:model-catalog-updated", refresh);
    return () => window.removeEventListener("herskin:model-catalog-updated", refresh);
  }, [load]);

  const change = async (candidateId: string) => {
    const candidate = catalog?.candidates.find((item) => item.candidateId === candidateId);
    if (!candidate || candidate.isPrimary) return;
    if (!window.confirm(`切换到“${candidate.displayName}”？只影响后续新会话，当前会话保持原模型。`))
      return;
    const commandId = `model_apply_${crypto.randomUUID()}`;
    setBusy(true);
    try {
      let result = ApplyPrimaryModelResponseSchema.parse(
        await api("/api/management/models/apply", {
          method: "POST",
          body: JSON.stringify({
            commandId,
            candidateId,
            revision: candidate.revision,
            confirmExpensiveModel: false,
          }),
        }),
      );
      if (result.state === "confirmation_required") {
        if (!window.confirm(result.message)) return;
        result = ApplyPrimaryModelResponseSchema.parse(
          await api("/api/management/models/apply", {
            method: "POST",
            body: JSON.stringify({
              commandId,
              candidateId,
              revision: candidate.revision,
              confirmExpensiveModel: true,
            }),
          }),
        );
      }
      setNotice(result.message);
      await load();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : "主模型切换没有完成");
    } finally {
      setBusy(false);
    }
  };

  const primary = catalog?.candidates.find((item) => item.isPrimary);
  const eligible =
    catalog?.candidates.filter(
      (item) => item.enabled && (item.isPrimary || item.test.state === "passed"),
    ) ?? [];
  return (
    <div className="composer-model-selector">
      <SelectField
        className="model-chip"
        ariaLabel="Hermes 全局主模型"
        value={primary?.candidateId ?? ""}
        placeholder="Hermes 主模型 · 读取中"
        disabled={busy || !catalog}
        options={eligible.map((candidate) => ({
          value: candidate.candidateId,
          label: candidate.displayName,
        }))}
        onValueChange={(value) => void change(value)}
      />
      {notice ? (
        <span className="composer-model-notice" role="status">
          {notice}
        </span>
      ) : null}
    </div>
  );
}
